import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Text,
  InlineStack,
  BlockStack,
  Badge,
  DataTable,
  Spinner,
  Banner,
  Button,
  TextField,
  InlineError,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { rentalsAPI, settingsAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentRentals, setRecentRentals] = useState([]);
  const [overdueRentals, setOverdueRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Settings state
  const [minDaysInAdvance, setMinDaysInAdvance] = useState(3);
  const [minBookingDays, setMinBookingDays] = useState(3);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchSettings();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching dashboard data...');
      console.log('🔍 API Base URL:', process.env.REACT_BACKEND_BASE_URL || 'https://shopify.dropkid.de');
      
      const [statsResponse, rentalsResponse, overdueResponse] = await Promise.all([
        rentalsAPI.getStats(),
        rentalsAPI.getAll({ limit: 5, recent: true }),
        rentalsAPI.getOverdue(),
      ]);

      console.log('✅ Dashboard data received:', {
        stats: statsResponse.data,
        rentals: rentalsResponse.data,
        overdue: overdueResponse.data
      });

      setStats(statsResponse.data);
      setRecentRentals(rentalsResponse.data.rentals || []);
      setOverdueRentals(overdueResponse.data || []);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('❌ Dashboard error:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { status: 'info', children: 'Draft' },
      pending: { status: 'info', children: 'Pending' },
      confirmed: { status: 'success', children: 'Confirmed' },
      active: { status: 'success', children: 'Active' },
      overdue: { status: 'critical', children: 'Overdue' },
      returned: { status: 'success', children: 'Returned' },
      cancelled: { status: 'warning', children: 'Cancelled' },
    };
    
    return <Badge {...statusConfig[status]} />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      setSettingsError('');
      const response = await settingsAPI.get();
      setMinDaysInAdvance(response.data.minDaysInAdvance || 3);
      setMinBookingDays(response.data.minBookingDays || 3);
    } catch (err) {
      setSettingsError('Failed to load settings');
      console.error('Settings error:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsError('');
      setSettingsSuccess(false);
      
      const numValue = parseInt(minDaysInAdvance, 10);
      if (isNaN(numValue) || numValue < 0) {
        setSettingsError('Minimum days in advance must be a non-negative number');
        setSettingsSaving(false);
        return;
      }
      
      const bookingDaysValue = parseInt(minBookingDays, 10);
      if (isNaN(bookingDaysValue) || bookingDaysValue < 0) {
        setSettingsError('Minimum booking days must be a non-negative number');
        setSettingsSaving(false);
        return;
      }
      
      await settingsAPI.update({ minDaysInAdvance: numValue, minBookingDays: bookingDaysValue });
      setSettingsSuccess(true);
      setMinDaysInAdvance(numValue);
      setMinBookingDays(bookingDaysValue);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) {
      setSettingsError(err.response?.data?.error || 'Failed to save settings');
      console.error('Save settings error:', err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const recentRentalsRows = (recentRentals || []).map((rental) => [
    rental.id,
    `#${rental.shopifyOrderId}`,
    rental.customerEmail,
    new Date(rental.startDate).toLocaleDateString(),
    new Date(rental.endDate).toLocaleDateString(),
    getStatusBadge(rental.status),
    formatCurrency(rental.total),
  ]);

  if (loading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <InlineStack align="center" gap="400">
              <Spinner key="spinner" size="large" />
              <Text key="loading-text">Loading dashboard...</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <InlineStack align="space-between">
          <Text variant="headingLg" as="h1">Rental Dashboard</Text>
          <InlineStack gap="200">
            <Text variant="bodySm" tone="subdued">v1.2.1</Text>
            <Button key="new-rental" onClick={() => navigate('/rentals')}>View Rental</Button>
            <Button key="manage-products" onClick={() => navigate('/products')}>Manage Products</Button>
          </InlineStack>
        </InlineStack>
      </div>
      {error && (
        <Layout>
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        {/* Stats Cards */}
        <Layout.Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '16px' }}>
            {[
              { label: 'Total Rentals', value: stats?.totalRentals || 0, url: '/current-rentals' },
              { label: 'Active Rentals', value: stats?.activeRentals || 0, url: '/rentals?status=active' },
              { label: 'Overdue Rentals', value: stats?.overdueRentals || 0, url: '/rentals?status=overdue', critical: true },
            ].map((item) => (
              <button
                key={item.url}
                type="button"
                onClick={() => navigate(item.url)}
                aria-label={`View ${item.label.toLowerCase()}`}
                style={{ padding: 0, border: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer' }}
              >
                <Card>
                  <BlockStack gap="200">
                    <Text variant="bodyMd" tone="subdued">{item.label}</Text>
                    <Text variant="headingMd" tone={item.critical && item.value > 0 ? 'critical' : undefined}>
                      {item.value}
                    </Text>
                  </BlockStack>
                </Card>
              </button>
            ))}
          </div>
        </Layout.Section>

        {/* Overdue Rentals Alert */}
        {(overdueRentals || []).length > 0 && (
          <Layout.Section>
            <Banner
              status="critical"
              title={`${(overdueRentals || []).length} rental(s) are overdue`}
              action={{
                content: 'View Overdue Rentals',
                onAction: () => navigate('/rentals?status=overdue'),
              }}
            >
              <p>These rentals need immediate attention.</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Recent Rentals */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingSm">Recent Rentals</Text>
                <Button
                  variant="plain"
                  onClick={() => navigate('/rentals')}
                >
                  View All
                </Button>
              </InlineStack>
              {(recentRentals || []).length > 0 ? (
                <DataTable
                  columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['ID', 'Order ID', 'Customer', 'Start Date', 'End Date', 'Status', 'Total']}
                  rows={recentRentalsRows}
                  footerContent={`Showing ${(recentRentals || []).length} of ${(recentRentals || []).length} rentals`}
                />
              ) : (
                <InlineStack align="center">
                  <Text key="no-rentals-text" variant="bodyMd" tone="subdued">No rentals found</Text>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Status Breakdown */}
        {stats?.statusBreakdown && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingSm">Rentals by Status</Text>
                <InlineStack gap="400" wrap>
                  {(stats.statusBreakdown || []).map((status, index) => (
                    <div key={status.id || `status-${index}`}>
                      <BlockStack gap="100">
                        <Text variant="bodyMd" fontWeight="bold">{status.label || status.id}</Text>
                        <Text variant="bodyMd">{status.count} rentals</Text>
                        <Text variant="bodyMd" tone="subdued">
                          {formatCurrency(status.totalAmount)} total
                        </Text>
                      </BlockStack>
                    </div>
                  ))}
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Settings Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingSm">Booking Settings</Text>
              {settingsLoading ? (
                <InlineStack align="center" gap="400">
                  <Spinner size="small" />
                  <Text variant="bodyMd" tone="subdued">Loading settings...</Text>
                </InlineStack>
              ) : (
                <BlockStack gap="300">
                  <TextField
                    label="Minimum Days in Advance"
                    type="number"
                    value={String(minDaysInAdvance)}
                    onChange={(value) => {
                      setMinDaysInAdvance(value);
                      setSettingsError('');
                      setSettingsSuccess(false);
                    }}
                    helpText="Customers must book at least this many days in advance. For example, if set to 3, customers can only book starting from 3 days from today."
                    min={0}
                    step={1}
                    autoComplete="off"
                  />
                  <TextField
                    label="Minimum Booking Days"
                    type="number"
                    value={String(minBookingDays)}
                    onChange={(value) => {
                      setMinBookingDays(value);
                      setSettingsError('');
                      setSettingsSuccess(false);
                    }}
                    helpText="Minimum range of days that may be selected in the search widget."
                    min={0}
                    step={1}
                    autoComplete="off"
                  />
                  {settingsError && (
                    <InlineError message={settingsError} fieldID="minDaysInAdvance" />
                  )}
                  {settingsSuccess && (
                    <Banner status="success" onDismiss={() => setSettingsSuccess(false)}>
                      Settings saved successfully!
                    </Banner>
                  )}
                  <InlineStack gap="200">
                    <Button
                      primary
                      onClick={handleSaveSettings}
                      loading={settingsSaving}
                      disabled={settingsSaving}
                    >
                      Save Settings
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </div>
  );
};

export default Dashboard;
